import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = ['residential', 'commercial', 'industrial'].flatMap((segment) => [
  { id: `${segment}-one-time`, segment, code: 'one-time', name: 'One-Time', description: 'Single focused treatment visit', features: ['Targeted treatment visit', 'Certified technician', 'Digital service record'], duration_months: 0, price_multiplier: 1, active: true },
  { id: `${segment}-3-month`, segment, code: '3-month', name: '3 Month', description: 'Three months of scheduled protection', features: ['Scheduled follow-up support', 'Digital service record'], duration_months: 3, price_multiplier: 2.7, active: true },
  { id: `${segment}-6-month`, segment, code: '6-month', name: '6 Month', description: 'Six months of ongoing protection', features: ['Scheduled follow-up support', 'Priority re-treatment guidance'], duration_months: 6, price_multiplier: 5.1, featured: true, active: true },
  { id: `${segment}-12-month`, segment, code: '12-month', name: '12 Month', description: 'A full year of preventive coverage', features: ['Scheduled follow-up support', 'Priority re-treatment guidance', 'Digital service record'], duration_months: 12, price_multiplier: 9.6, active: true },
  { id: `${segment}-custom`, segment, code: 'custom', name: 'Custom', description: 'Choose your coverage period', is_custom_duration: true, custom_min_months: 2, custom_max_months: 24, monthly_multiplier: 0.9, active: true },
]);

let cachedPlans = null;
let inFlight = null;

export function notifyServicePlansUpdated() {
  cachedPlans = null;
  if (typeof window !== 'undefined') { window.dispatchEvent(new Event('service-plans-updated')); try { window.localStorage.setItem('service-plans-updated-at', String(Date.now())); } catch (_) {} }
}

export async function fetchServicePlans(force = false) {
  if (cachedPlans && !force) return cachedPlans;
  if (!inFlight) {
    inFlight = axios.get(`${API_BASE}/service-plans`)
      .then((res) => { cachedPlans = res.data?.items || FALLBACK; return cachedPlans; })
      .catch(() => FALLBACK)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

export function useServicePlans(segment, pestCategory = 'general-pest') {
  const matches = (plan) => plan.segment === segment && (plan.pest_category || 'general-pest') === pestCategory;
  const [plans, setPlans] = useState(FALLBACK.filter(matches));
  useEffect(() => {
    let active = true;
    const refresh = () => fetchServicePlans(true).then((items) => {
      if (active) setPlans(items.filter((plan) => matches(plan) && plan.active));
    });
    fetchServicePlans().then((items) => { if (active) setPlans(items.filter((plan) => matches(plan) && plan.active)); });
    window.addEventListener('service-plans-updated', refresh);
    const timer = window.setInterval(refresh, 3000);
    return () => { active = false; window.removeEventListener('service-plans-updated', refresh); window.clearInterval(timer); };
  }, [segment, pestCategory]);
  return useMemo(() => plans.filter((plan) => plan.active), [plans]);
}

export function calculatePlanAmount(baseAmount, plan, customMonths) {
  if (!Number.isFinite(baseAmount) || baseAmount <= 0 || !plan) return baseAmount || 0;
  const multiplier = plan.is_custom_duration
    ? (Number(plan.monthly_multiplier) || 1) * Number(customMonths || plan.custom_min_months || 1)
    : (Number(plan.price_multiplier) || 1);
  const amount = plan.pricing_mode === 'fixed'
    ? Number(plan.fixed_amount) || 0
    : Math.round(baseAmount * multiplier);
  return Math.max(amount, Number(plan.minimum_amount) || 0);
}